/**
 * salesByDimensionDataSource.ts — WatermelonDB → SalesByDimensionInput bridge.
 *
 * SALE + CREDIT_NOTE invoices raised in the range (voided excluded) plus their
 * lines, with product→category and customer→name lookups. Accrual basis, like
 * the Sales report it slices.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, LineItem, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Product, Category } from '@/db/models/Inventory';
import { Customer } from '@/db/models/Customers';
import type { SalesByDimensionInput } from './salesByDimension';

export async function loadSalesByDimension(
  storeId: string,
  from: Date,
  to: Date,
): Promise<SalesByDimensionInput> {
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', Q.oneOf(['SALE', 'CREDIT_NOTE'])),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lt(to.getTime())),
  ).fetch();

  const typeByInvoiceId = new Map(invoices.map((i) => [i.id, i.type]));
  const invoiceIds = invoices.map((i) => i.id);

  const lineRows = await fetchInChunks(invoiceIds, (batch) =>
    database.get<LineItem>('line_items').query(
      Q.where('invoice_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );

  // Product → category.
  const productIds = [...new Set(lineRows.map((li) => li.productId))];
  const productRows = await fetchInChunks(productIds, (batch) =>
    database.get<Product>('products').query(Q.where('id', Q.oneOf(batch)), Q.where('is_deleted', false)).fetch(),
  );
  const categoryIds = [...new Set(productRows.map((p) => p.categoryId).filter((id): id is string => !!id))];
  const categoryRows = await fetchInChunks(categoryIds, (batch) =>
    database.get<Category>('categories').query(Q.where('id', Q.oneOf(batch)), Q.where('is_deleted', false)).fetch(),
  );
  const categoryName = new Map(categoryRows.map((c) => [c.id, c.name]));
  const productCategory = new Map(
    productRows.map((p) => [p.id, (p.categoryId && categoryName.get(p.categoryId)) || 'Uncategorised']),
  );

  // Customer → name.
  const customerIds = [...new Set(invoices.map((i) => i.customerId).filter((id): id is string => !!id))];
  const customerRows = await fetchInChunks(customerIds, (batch) =>
    database.get<Customer>('customers').query(Q.where('id', Q.oneOf(batch)), Q.where('is_deleted', false)).fetch(),
  );
  const customerName = new Map(customerRows.map((c) => [c.id, c.name?.trim() || c.phone || 'Unknown customer']));

  return {
    invoices: invoices.map((i) => ({
      type: i.type,
      totalAmount: i.totalAmount,
      createdAt: i.createdAt,
      customerId: i.customerId,
    })),
    lines: lineRows.map((li) => ({
      productId: li.productId,
      totalAmount: li.totalAmount,
      quantity: li.quantity,
      invoiceType: typeByInvoiceId.get(li.invoiceId) ?? 'SALE',
    })),
    productCategory,
    customerName,
  };
}
